import { useState, useRef, useEffect } from 'react'
import { formatRecordingTime } from '../utils/formatters'

/* SF Symbol Icons */
const PlayCircleFill = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 13 13" fill="currentColor">
    <path d="M6.05273 12.1055C2.71289 12.1055 0 9.39258 0 6.05273C0 2.71289 2.71289 0 6.05273 0C9.39258 0 12.1055 2.71289 12.1055 6.05273C12.1055 9.39258 9.39258 12.1055 6.05273 12.1055ZM4.99219 8.4375L8.37305 6.44531C8.67773 6.26953 8.67188 5.84766 8.37305 5.66602L4.99219 3.66797C4.68164 3.48633 4.2832 3.63867 4.2832 3.99023V8.11523C4.2832 8.47266 4.66406 8.63672 4.99219 8.4375Z"/>
  </svg>
)

const SFArrowUp = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 12 14" fill="currentColor">
    <path d="M6 0.5C6.28906 0.5 6.52344 0.673828 6.69336 0.908203L11.0625 6.74219C11.1797 6.91211 11.25 7.08203 11.25 7.27539C11.25 7.69336 10.9453 8.02148 10.5332 8.02148C10.293 8.02148 10.0996 7.92188 9.94727 7.72266L6.99023 4.09375L6.77344 3.70312L6.80859 4.51172V12.7734C6.80859 13.1914 6.52148 13.5 6 13.5C5.47852 13.5 5.19141 13.1914 5.19141 12.7734V4.51172L5.22656 3.70312L5.00977 4.09375L2.05273 7.72266C1.90039 7.92188 1.70703 8.02148 1.46680 8.02148C1.05469 8.02148 0.75 7.69336 0.75 7.27539C0.75 7.08203 0.820312 6.91211 0.9375 6.74219L5.30664 0.908203C5.47656 0.673828 5.71094 0.5 6 0.5Z"/>
  </svg>
)

/* Custom pause/stop button — darker blue circle with rounded square inside */
const DarkBlueStopButton = ({ onClick, size = 28 }) => (
  <button onClick={onClick} style={{
    width: `${size}px`, height: `${size}px`, borderRadius: '50%',
    background: '#0a2a5c', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, padding: 0,
  }}>
    <div style={{
      width: `${size * 0.38}px`, height: `${size * 0.38}px`,
      borderRadius: `${size * 0.1}px`,
      background: 'var(--color-primary)',
    }} />
  </button>
)

function VoiceRecorder({ recording, voiceReady, voicePreviewUrl, recordingTime, waveformData, voiceSlideOffset, onStopRecording, onCancelVoice, onConfirmVoice }) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const audioRef = useRef(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTime = () => {
      if (audio.duration) setProgress(audio.currentTime / audio.duration)
    }
    audio.addEventListener('timeupdate', onTime)
    return () => audio.removeEventListener('timeupdate', onTime)
  }, [voicePreviewUrl])

  const togglePlay = () => {
    if (!audioRef.current) return
    if (playing) {
      audioRef.current.pause()
      setPlaying(false)
    } else {
      audioRef.current.play()
      setPlaying(true)
    }
  }

  if (!recording && !voiceReady) return null

  /* ── Recording state ── */
  if (recording) return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '1.5px',
      height: '36px', overflow: 'hidden', flex: 1,
      transform: `translateX(${voiceSlideOffset}px)`,
      transition: voiceSlideOffset === 0 ? 'transform 0.25s ease' : 'none',
    }}>
      {waveformData.map((h, i) => (
        <div key={i} style={{
          width: '2.5px', height: `${h}px`, borderRadius: '1.5px',
          background: 'rgba(255,255,255,0.5)', flexShrink: 0,
          transition: 'height 0.08s ease',
        }} />
      ))}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, paddingLeft: '10px' }}>
        <span style={{
          color: 'var(--color-text-secondary)', fontSize: '13px',
          fontVariantNumeric: 'tabular-nums', minWidth: '38px', textAlign: 'right',
        }}>{formatRecordingTime(recordingTime)}</span>
        <DarkBlueStopButton onClick={() => onStopRecording(false)} size={32} />
      </div>
    </div>
  )

  /* ── Preview state ── */
  if (voiceReady && voicePreviewUrl) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, height: '36px' }}>
      <audio ref={audioRef} src={voicePreviewUrl} onEnded={() => { setPlaying(false); setProgress(0) }} style={{ display: 'none' }} />

      {/* Play/Pause button — same size as send button (32x32) */}
      <div style={{ marginLeft: '-8px', flexShrink: 0, marginBottom: '-2px' }}>
        {playing ? (
          <DarkBlueStopButton onClick={togglePlay} size={32} />
        ) : (
          <button onClick={togglePlay} style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', flexShrink: 0, padding: 0,
          }}>
            <PlayCircleFill size={32} />
          </button>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1.5px', height: '28px', overflow: 'hidden' }}>
        {waveformData.map((h, i) => {
          const playedThreshold = Math.floor(waveformData.length * progress)
          const isPlayed = i < playedThreshold
          return (
            <div key={i} style={{
              width: '2px',
              height: `${Math.max(3, h * 0.8)}px`,
              borderRadius: '1px',
              background: isPlayed ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
              flexShrink: 0,
              transition: 'background 0.1s linear',
            }} />
          )
        })}
      </div>

      <span style={{
        color: 'var(--color-text-secondary)', fontSize: '12px',
        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
        background: 'rgba(255,255,255,0.08)', borderRadius: '9px',
        padding: '2px 8px',
      }}>+ {formatRecordingTime(recordingTime)}</span>

      {/* Send button — matches InputBar SF Symbol arrow.up exactly */}
      <button onClick={onConfirmVoice} style={{
        width: '32px', height: '32px', borderRadius: '50%',
        background: 'var(--color-primary)', border: 'none',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginBottom: '0px', color: '#fff', padding: 0,
      }}>
        <SFArrowUp size={18} />
      </button>
    </div>
  )

  return null
}

export default VoiceRecorder